import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const nl = {
  translation: {
    common: {
      login: "Inloggen",
      logout: "Uitloggen",
      language: "Taal",
      back: "Terug",
      confirm: "Bevestigen",
      cancel: "Annuleren",
      save: "Opslaan",
      add: "Toevoegen",
      delete: "Verwijderen",
      close: "Sluiten",
      loading: "Laden…",
      control: "Control",
      vehicleApp: "Voertuig-app",
      commandCenter: "Command Center",
    },
    nav: {
      dashboard: "Dashboard",
      incidents: "Incidenten",
      vehicles: "Voertuigen",
      map: "Kaart",
      reports: "Rapportage",
      knowledgebase: "Kennisbank",
      archive: "Archief",
      procedures: "Procedures",
      proceduresTip: "Toon relevante procedures voor dit incident",
      proceduresDisabled: "Open een incident om procedures te zien",
      beheer: "Beheer",
      tablet: "Tablet",
      pager: "Pager",
      monitor: "Monitor",
    },
    role: {
      beheerder: "Beheerder",
      meldkamer: "Meldkamer",
      voertuig: "Voertuig",
      monitor: "Monitor",
    },
    ws: {
      live: "Live",
      reconnecting: "Herverbinden…",
      connecting: "Verbinden…",
      liveTitle: "Realtime verbinding actief",
      degradedTitle: "Verbinding hapert — bezig met herverbinden",
      connectingTitle: "Verbinding maken met realtime server",
      connected: "Verbonden",
    },
    login: {
      title: "Inloggen",
      subtitle: "Log in met je e-mail en wachtwoord.",
      email: "E-mail",
      password: "Wachtwoord",
      forgot: "Wachtwoord vergeten?",
      headline: "Realtime\nincidentbeheer\nvoor de meldkamer.",
      tagline: "Coördineer incidenten, voertuigen en communicatie tussen commandocentrum en voertuigen — live gesynchroniseerd.",
      defenseCenter: "Command Center",
      prototype: "Prototype / MVP · niet-geaccrediteerde omgeving",
      demoAccounts: "Demo-accounts",
      installApps: "Apps installeren — scan",
      installApp: "Installeer meldkamer-app",
      installHint: "Open het menu van je browser (⋮ → App installeren) om DispatchMe als app te installeren.",
      badge: "MELDKAMER · LIVE C2",
      live: "Live verbonden",
      features: ["Realtime incidenten & voertuigen", "Interactieve kaart met live GPS", "Live video vanaf de melder", "TOTP-beveiliging & apparaatbeheer"],
      scanApps: "Apps scannen & installeren",
      scan: {
        title: "Welke app wil je installeren?",
        subtitle: "Kies een app om de QR-code te tonen.",
        instruct: "Scan met de camera van het doelapparaat.",
        back: "← Andere app",
        open: "Openen",
        apps: {
          pager: { label: "Pager", desc: "Alarmering op de telefoon" },
          monitor: { label: "Kazernemonitor", desc: "Groot scherm in de kazerne" },
          voertuig: { label: "Voertuig-app", desc: "Tablet in het voertuig" }
        }
      },
      verify: "Verifiëren",
      scanAuth: "Scan met je authenticator-app",
      mfaEmail: "Voer de 6-cijferige code in die we per e-mail stuurden.",
      mfaApp: "Voer de 6-cijferige code uit je authenticator-app in.",
      welcome: "Welkom, {{name}}",
    },
    vehicle: {
      title: "Voertuig-app",
      shareCamera: "Deel je camera",
      watchLive: "Live meekijken",
    },
    pager: {
      title: "Pager",
      standby: "Stand-by",
      confirm: "Bevestigen",
    },
    monitor: {
      title: "Kazernemonitor",
    },
    landing: {
      nav: { features: "Functies", workflow: "Werkwijze", security: "Veiligheid", devices: "PWA & App", login: "Inloggen" },
      hero: {
        badge: "MELDKAMER · C2-PLATFORM",
        headline: "Realtime incidentbeheer voor meldkamers",
        tagline: "DispatchMe verbindt de meldkamer naadloos met response-voertuigen — live video vanaf telefoons, interactieve kaarten, AI-rapportage en TOTP-beveiliging.",
        primaryCta: "Naar inloggen",
        secondaryCta: "Bekijk functies",
      },
      preview: { label: "LIVE C2-FEED", incident: "Incident", priority: "Prioriteit", video: "Videostream", eta: "Voertuig ETA", connected: "Verbonden", online: "Online" },
      features: {
        title: "Ontworpen voor mission-critical operaties",
        subtitle: "Alles wat een meldkamer en een responseteam in het veld nodig hebben.",
        items: [
          { title: "Realtime dispatch via WebSockets", desc: "Directe updates van incidentstatus, eenheidstoewijzing en prioriteit — zonder verversen.", highlight: "~0,1s latentie" },
          { title: "Live video van de beller", desc: "Direct meekijken via een eenmalige, versleutelde videolink op de telefoon van de melder.", highlight: "Geen app nodig" },
          { title: "Interactieve Leaflet-kaarten", desc: "Live voertuig-GPS, incidentlocaties, geofencing en kazerne-overlays.", highlight: "GPS live" },
          { title: "AI-incidentrapportage", desc: "Automatische samenvatting van notities, tijdlijnen en logboeken tot een officieel rapport.", highlight: "AI Assist" },
          { title: "TOTP / MFA & apparaatvergrendeling", desc: "Sterke authenticatie met 2FA-token, sessiebewaking en gekoppelde apparaten.", highlight: "Veilig" },
          { title: "Voertuig-PWA & kazernemonitor", desc: "Speciale tablet-interface voor voertuigen plus grootscherm kazerne-displays.", highlight: "PWA ready" },
          { title: "Kennisbank & pager", desc: "Directe toegang tot protocollen, gevaarlijke stoffen en stille pager-alarmering.", highlight: "Snelle referentie" },
        ],
      },
      workflow: {
        title: "Van melding tot afhandeling in 4 stappen",
        subtitle: "Gestroomlijnde communicatie tussen burger, meldkamer en responder.",
        steps: [
          { title: "Melding & AI-intake", desc: "Inkomende oproep wordt direct geregistreerd. AI verrijkt urgentie en geolocatie." },
          { title: "Live videolink", desc: "Link naar de beller voor een directe HD-videostream naar het meldkamerscherm." },
          { title: "Voertuig-dispatch", desc: "Voertuigen ontvangen de oproep op hun tablet-PWA met realtime navigatie en details." },
          { title: "Status & rapport", desc: "Voertuig meldt status (ter plaatse / vrij). AI genereert automatisch de inzetrapportage." },
        ],
      },
      security: {
        title: "Veiligheid & privacy",
        subtitle: "Ontworpen met strenge protocollen voor soevereine dataveiligheid.",
        badges: ["TOTP 2FA-authenticatie", "Secret-key guard", "Nederlandse soevereiniteit", "Audit-trail logboeken", "Versleutelde WebSockets", "PWA offline caching"],
      },
      footer: {
        brand: "DispatchMe — Mission-critical incidentbeheer voor meldkamers.",
        disclaimer: "DispatchMe is een demonstratie- en prototypemodel. Niet geaccrediteerd voor echte noodoproepen.",
        copyright: "© 2026 DispatchMe NL. Alle rechten voorbehouden.",
      },
    },
    dash: {
      title: "Dashboard",
      overview: "Live operationeel overzicht",
      pickPeriod: "kies een periode",
      newIncident: "Incident aanmaken",
      newIncidentTitle: "Nieuw incident aanmaken",
      ranges: { dienst: "Dienst (vandaag)", week: "Deze week", maand: "Deze maand", custom: "Van–tot" },
      period: { dienst: "Deze dienst", week: "Deze week", maand: "Deze maand", custom: "Periode" },
      to: "t/m",
      kpi: { open: "Open incidenten", closed: "Gesloten (periode)", activeVehicles: "Actieve voertuigen", incidents: "Incidenten", avgClose: "Gem. tijd-tot-sluiten", avgResponse: "Gem. responstijd" },
      byPriority: "Open incidenten per prioriteit",
      activeIncidents: "Actieve incidenten",
      viewAll: "Alles bekijken",
      noOpen: "Geen open incidenten",
      vehiclesCount: "{{count}} voertuig(en)",
      vehicleStatus: "Voertuigstatus",
    },
    dev: {
      loading: "Laden…",
      enterCode: "Koppelcode invoeren",
      pairDevice: "Apparaat koppelen",
      scanQr: "QR-code scannen",
      stopScan: "Scannen stoppen",
      cancel: "Annuleren",
      unpair: "Apparaat ontkoppelen",
      pagerEnrollHint: "Voer de éénmalige code in (of scan de QR uit Control). De pager alarmeert daarna op de gekoppelde locatie.",
      pagerArmHint: "Tik om de pager te activeren. Dit schakelt het geluidsalarm in — nodig omdat de browser geluid pas na een tik toestaat.",
      monTitle: "Kazernemonitor koppelen",
      monEnrollHint: "Voer de éénmalige koppelcode in (of scan de QR uit Beheer · Control). De monitor draait daarna zonder personeelslogin.",
      monArmHint: "Tik om de monitor te starten. Dit schakelt de gesproken omroep in (de browser staat spraak pas na een tik toe).",
      codeLabel: "Koppelcode",
      monPair: "Monitor koppelen",
      monStart: "Monitor starten",
      monUnpair: "Monitor ontkoppelen",
      vehPendingTitle: "Wachten op goedkeuring",
      vehPendingHint: "Dit apparaat is aangemeld. De meldkamer moet het in Control goedkeuren. Dit scherm ververst automatisch.",
      vehCancelPending: "Annuleren / andere code",
      vehEnrollHint: "Voer de éénmalige code in (of scan de QR uit Control). Daarna moet de meldkamer dit apparaat goedkeuren.",
      scanAimHint: "Richt de camera op de QR-code uit Beheer › Control.",
    },
    inc: {
      title: "Incidenten", openCount: "{{count}} open incident(en)", newIncident: "Nieuw incident",
      emptyList: "Geen open incidenten. Maak een nieuw incident aan.", none: "geen",
      titleLabel: "Melding", manageTitles: "Titels beheren", titlePh: "Korte omschrijving — kies of typ",
      manageHeader: "Titels beheren (centralist)", newTitlePh: "Nieuwe titel…", linkType: "— koppel type —", add: "Toevoegen",
      bulkPh: "Of plak een lijst — één titel per regel (gebruikt het gekozen type hierboven)", noType: "— geen type —", twoSided: "2-zijdig",
      typeLabel: "Type", autoViaTitle: "automatisch via titel", locLabel: "Locatie", building: "Gebouw", mapPin: "Kaart-pin",
      buildingSearchPh: "Zoek gebouw op naam, functie of code…", noBuildings: "Geen gebouwen gevonden",
      pickBuilding: "Kies een gebouw om de locatie op de kaart te zien", pinLabelPh: "Straatnaam / omschrijving locatie", clickMap: "klik op de kaart",
      priority: "Prioriteit", description: "Omschrijving", descPh: "Aanvullende details… of gebruik 'Inspreken'",
      assignVehicles: "Voertuigen toewijzen", createBtn: "Incident aanmaken", vehSearchPh: "Zoek op roepnummer of naam…", allLocations: "Alle locaties", noVehiclesFound: "Geen voertuigen gevonden",
      pickBuildingErr: "Kies een gebouw", setPinErr: "Zet een pin op de kaart", created: "Incident {{ref}} aangemaakt",
      enterTitleErr: "Voer een titel in", titlesAdded: "Titel(s) toegevoegd", mapLocation: "Locatie op kaart",
    },
  },
};

const en = {
  translation: {
    common: {
      login: "Sign in",
      logout: "Sign out",
      language: "Language",
      back: "Back",
      confirm: "Confirm",
      cancel: "Cancel",
      save: "Save",
      add: "Add",
      delete: "Delete",
      close: "Close",
      loading: "Loading…",
      control: "Control",
      vehicleApp: "Vehicle app",
      commandCenter: "Command Center",
    },
    nav: {
      dashboard: "Dashboard",
      incidents: "Incidents",
      vehicles: "Vehicles",
      map: "Map",
      reports: "Reports",
      knowledgebase: "Knowledge base",
      archive: "Archive",
      procedures: "Procedures",
      proceduresTip: "Show relevant procedures for this incident",
      proceduresDisabled: "Open an incident to see procedures",
      beheer: "Admin",
      tablet: "Tablet",
      pager: "Pager",
      monitor: "Monitor",
    },
    role: {
      beheerder: "Administrator",
      meldkamer: "Dispatch",
      voertuig: "Vehicle",
      monitor: "Monitor",
    },
    ws: {
      live: "Live",
      reconnecting: "Reconnecting…",
      connecting: "Connecting…",
      liveTitle: "Realtime connection active",
      degradedTitle: "Connection unstable — reconnecting",
      connectingTitle: "Connecting to realtime server",
      connected: "Connected",
    },
    login: {
      title: "Sign in",
      subtitle: "Sign in with your email and password.",
      email: "Email",
      password: "Password",
      forgot: "Forgot password?",
      headline: "Realtime\nincident management\nfor the dispatch center.",
      tagline: "Coordinate incidents, vehicles and communication between the command center and vehicles — synced live.",
      defenseCenter: "Command Center",
      prototype: "Prototype / MVP · non-accredited environment",
      demoAccounts: "Demo accounts",
      installApps: "Install apps — scan",
      installApp: "Install command-center app",
      installHint: "Open your browser menu (⋮ → Install app) to install DispatchMe as an app.",
      badge: "DISPATCH · LIVE C2",
      live: "Live connected",
      features: ["Real-time incidents & vehicles", "Interactive map with live GPS", "Live video from the caller", "TOTP security & device control"],
      scanApps: "Scan & install apps",
      scan: {
        title: "Which app do you want to install?",
        subtitle: "Choose an app to show its QR code.",
        instruct: "Scan with the target device's camera.",
        back: "← Other app",
        open: "Open",
        apps: {
          pager: { label: "Pager", desc: "Alerts on the phone" },
          monitor: { label: "Station monitor", desc: "Large screen at the station" },
          voertuig: { label: "Vehicle app", desc: "Tablet in the vehicle" }
        }
      },
      verify: "Verify",
      scanAuth: "Scan with your authenticator app",
      mfaEmail: "Enter the 6-digit code we emailed you.",
      mfaApp: "Enter the 6-digit code from your authenticator app.",
      welcome: "Welcome, {{name}}",
    },
    vehicle: {
      title: "Vehicle app",
      shareCamera: "Share your camera",
      watchLive: "Watch live",
    },
    pager: {
      title: "Pager",
      standby: "Standby",
      confirm: "Acknowledge",
    },
    monitor: {
      title: "Station monitor",
    },
    landing: {
      nav: { features: "Features", workflow: "Workflow", security: "Security", devices: "PWA & Devices", login: "Login" },
      hero: {
        badge: "DISPATCH · C2 PLATFORM",
        headline: "Real-time incident management for command centers",
        tagline: "DispatchMe seamlessly connects command centers with response vehicles — live video from phones, interactive maps, AI reporting and TOTP security.",
        primaryCta: "Go to login",
        secondaryCta: "Explore features",
      },
      preview: { label: "LIVE C2 FEED", incident: "Incident", priority: "Priority", video: "Video stream", eta: "Vehicle ETA", connected: "Connected", online: "Online" },
      features: {
        title: "Built for mission-critical operations",
        subtitle: "Everything a command center operator and a field response unit need.",
        items: [
          { title: "Real-time dispatch via WebSockets", desc: "Instant incident status, unit assignment and priority updates — without a refresh.", highlight: "~0.1s latency" },
          { title: "Live mobile video stream", desc: "Watch live via a single-use, encrypted video link sent to the caller's phone.", highlight: "No app needed" },
          { title: "Interactive Leaflet maps", desc: "Live vehicle GPS, incident coordinates, geofencing and station overlays.", highlight: "GPS live" },
          { title: "AI incident reporting", desc: "Automated summaries of notes, timelines and logs into an official report.", highlight: "AI Assist" },
          { title: "TOTP / MFA & device locks", desc: "Strong authentication with 2FA tokens, session locking and device enrollment.", highlight: "Secure" },
          { title: "Vehicle PWA & station monitor", desc: "Dedicated tablet interface for response vehicles plus large-screen station displays.", highlight: "PWA ready" },
          { title: "Knowledge base & pager", desc: "Instant access to protocols, hazardous-material sheets and silent pager alerts.", highlight: "Quick reference" },
        ],
      },
      workflow: {
        title: "From alarm to resolution in 4 steps",
        subtitle: "Streamlined communication between citizen, command center and unit.",
        steps: [
          { title: "Incident & AI intake", desc: "Incoming call registered instantly. AI enriches priority and geolocation." },
          { title: "Live video link", desc: "Link sent to the caller for an instant HD video stream to the operator dashboard." },
          { title: "Vehicle dispatch", desc: "Units receive the alert on their tablet PWA with real-time route guidance and details." },
          { title: "Status & report", desc: "Unit logs status (on scene / clear). AI automatically generates the operational summary." },
        ],
      },
      security: {
        title: "Security & privacy",
        subtitle: "Designed with strict protocols for sovereign data integrity.",
        badges: ["TOTP 2FA authentication", "Secret-key guard", "Dutch sovereignty", "Audit-trail logging", "Encrypted WebSockets", "PWA offline caching"],
      },
      footer: {
        brand: "DispatchMe — Mission-critical incident management for command centers.",
        disclaimer: "DispatchMe is a prototype and demonstration system. Not accredited for live emergency calls.",
        copyright: "© 2026 DispatchMe NL. All rights reserved.",
      },
    },
    dash: {
      title: "Dashboard",
      overview: "Live operational overview",
      pickPeriod: "choose a period",
      newIncident: "New incident",
      newIncidentTitle: "Create new incident",
      ranges: { dienst: "Shift (today)", week: "This week", maand: "This month", custom: "From–to" },
      period: { dienst: "This shift", week: "This week", maand: "This month", custom: "Period" },
      to: "to",
      kpi: { open: "Open incidents", closed: "Closed (period)", activeVehicles: "Active vehicles", incidents: "Incidents", avgClose: "Avg. time-to-close", avgResponse: "Avg. response time" },
      byPriority: "Open incidents by priority",
      activeIncidents: "Active incidents",
      viewAll: "View all",
      noOpen: "No open incidents",
      vehiclesCount: "{{count}} vehicle(s)",
      vehicleStatus: "Vehicle status",
    },
    dev: {
      loading: "Loading…",
      enterCode: "Enter pairing code",
      pairDevice: "Pair device",
      scanQr: "Scan QR code",
      stopScan: "Stop scanning",
      cancel: "Cancel",
      unpair: "Unpair device",
      pagerEnrollHint: "Enter the single-use code (or scan the QR from Control). The pager then alerts on the linked location.",
      pagerArmHint: "Tap to activate the pager. This enables the audible alarm — required because the browser only allows sound after a tap.",
      monTitle: "Pair station monitor",
      monEnrollHint: "Enter the single-use pairing code (or scan the QR from Admin · Control). The monitor then runs without a staff login.",
      monArmHint: "Tap to start the monitor. This enables spoken announcements (the browser only allows speech after a tap).",
      codeLabel: "Pairing code",
      monPair: "Pair monitor",
      monStart: "Start monitor",
      monUnpair: "Unpair monitor",
      vehPendingTitle: "Waiting for approval",
      vehPendingHint: "This device is registered. Dispatch must approve it in Control. This screen refreshes automatically.",
      vehCancelPending: "Cancel / other code",
      vehEnrollHint: "Enter the single-use code (or scan the QR from Control). Dispatch must then approve this device.",
      scanAimHint: "Point the camera at the QR code from Admin › Control.",
    },
    inc: {
      title: "Incidents", openCount: "{{count}} open incident(s)", newIncident: "New incident",
      emptyList: "No open incidents. Create a new incident.", none: "none",
      titleLabel: "Report", manageTitles: "Manage titles", titlePh: "Short description — pick or type",
      manageHeader: "Manage titles (dispatcher)", newTitlePh: "New title…", linkType: "— link type —", add: "Add",
      bulkPh: "Or paste a list — one title per line (uses the type selected above)", noType: "— no type —", twoSided: "2-sided",
      typeLabel: "Type", autoViaTitle: "auto via title", locLabel: "Location", building: "Building", mapPin: "Map pin",
      buildingSearchPh: "Search building by name, function or code…", noBuildings: "No buildings found",
      pickBuilding: "Pick a building to see the location on the map", pinLabelPh: "Street name / location description", clickMap: "click on the map",
      priority: "Priority", description: "Description", descPh: "Additional details… or use 'Dictate'",
      assignVehicles: "Assign vehicles", createBtn: "Create incident", vehSearchPh: "Search by call sign or name…", allLocations: "All locations", noVehiclesFound: "No vehicles found",
      pickBuildingErr: "Pick a building", setPinErr: "Drop a pin on the map", created: "Incident {{ref}} created",
      enterTitleErr: "Enter a title", titlesAdded: "Title(s) added", mapLocation: "Location on map",
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { nl, en },
    fallbackLng: "nl",
    supportedLngs: ["nl", "en"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "lang",
      caches: ["localStorage"],
    },
  });

export default i18n;
